// lib/apps/usoApps.js
// Uso de las apps: cuánto las usa cada persona, cuántos días vuelve y si de verdad las usa o
// solo las deja abiertas. Se ve en el admin, Analytics > Uso de apps.
//
// Cada página de /apps/<slug> llama a abrirApp() cuando la app ya se ve (pasó el cartel de
// acceso) y devuelve su resultado desde el efecto, así la sesión se cierra al irse:
//
//   useEffect(() => {
//     if (accessState !== "allowed") return;
//     return abrirApp("mosca");
//   }, [accessState]);
//
// Se cuentan tres tiempos, uno adentro del otro:
//   abierta   la app cargada, aunque sea en otra pestaña (hasta 30 min escondida)
//   visible   con la pestaña a la vista
//   usando    a la vista y con un clic, toque, tecla, rueda o movimiento del mouse en el
//             último minuto. Las apps que se usan sin tocar la pantalla (Aprender partitura
//             escucha el instrumento, el metrónomo suena) avisan con usandoApp().
// Más las interacciones: clics, toques y teclas.
//
// Cada reporte manda los TOTALES de la sesión y el back guarda el mayor, así que uno repetido
// o perdido no desarma la cuenta. Va cada minuto y al esconder o cerrar la pestaña, por
// dominio propio (app/api/uso-apps), como reflink.js: sin preflight de CORS y sin que un
// bloqueador lo corte por ir a otro host.

import { sendGA } from "@/lib/analytics";

const RUTA_REPORTE = "/api/uso-apps";
const CLAVE_VISITANTE = "bcm_uso_v";
const VISITANTE_RE = /^[A-Za-z0-9-]{8,40}$/;

const TICK_MS = 5000;
const REPORTE_MS = 60000;
// Un clic cuenta como "usándola" durante el minuto siguiente: el que lee un acorde y lo toca
// en la guitarra no está tocando la pantalla, pero la está usando.
const VENTANA_USO_MS = 60000;
// Más que esto entre dos ticks es que la compu durmió o el navegador congeló la pestaña: ese
// rato no cuenta para nada.
const SALTO_MAX_MS = 150000;
// Escondida más de esto, la sesión terminó: deja de contar como abierta, y si vuelve a la
// pestaña es una sesión nueva (que puede caer en otro día).
const CORTE_SESION_MS = 30 * 60000;

// Las que cuentan como interacción. El resto (rueda, movimiento, scroll, tipeo en un campo)
// solo marca que hay alguien.
const EVENTOS_INTERACCION = ["pointerdown", "keydown"];
const EVENTOS_ACTIVIDAD = ["wheel", "pointermove", "scroll", "input"];

let actual = null;
let visitanteSinGuardar = null;

/**
 * Arranca a medir la app. Devuelve la función que la cierra (para devolverla desde el
 * useEffect). Manda también el `app_open` de GA4, que antes mandaba cada página.
 */
export function abrirApp(app) {
  if (typeof window === "undefined") return () => {};
  sendGA("app_open", { app_name: app });
  if (actual) cerrar(actual);
  const sesion = nuevaSesion(app);
  actual = sesion;
  return () => {
    // Si volvió a la pestaña después de mucho, la que está midiendo es la sesión que
    // reemplazó a esta (cambioDeVisibilidad): se cierra la última de la cadena.
    let ultima = sesion;
    while (ultima.reemplazo) ultima = ultima.reemplazo;
    if (actual === ultima) actual = null;
    cerrar(ultima);
  };
}

/** Para las apps que se usan sin tocar la pantalla: cuenta como un rato de uso. */
export function usandoApp() {
  if (actual) actual.ultimaActividad = performance.now();
}

function nuevaSesion(app) {
  const ahora = performance.now();
  const s = {
    id: nuevoId(),
    app,
    dia: diaLocal(),
    // Una vez al abrir: al cerrar, la URL ya puede ser la de la página siguiente (navegación
    // de Next) y el documento que se va no siempre contesta bien matchMedia.
    locale: localeDeLaRuta(),
    dispositivo: dispositivo(),
    abierta: 0,
    visible: 0,
    usando: 0,
    interacciones: 0,
    ultimo: ahora,
    // Abrirla no cuenta como usarla: el que la abre y se va no tiene que sumar el minuto
    // gratis. Con mover el mouse adentro alcanza.
    ultimaActividad: -Infinity,
    visibleAntes: estaVisible(),
    escondidaDesde: estaVisible() ? null : Date.now(),
    reportado: "",
    identificada: false,
    token: null,
    escuchas: [],
    documentos: new WeakSet(),
    timer: null,
  };

  const actividad = () => {
    s.ultimaActividad = performance.now();
  };
  const interaccion = () => {
    s.ultimaActividad = performance.now();
    s.interacciones++;
  };
  s.engancharDocumento = (doc) => {
    if (!doc || s.documentos.has(doc)) return;
    s.documentos.add(doc);
    for (const tipo of EVENTOS_INTERACCION) escuchar(s, doc, tipo, interaccion);
    for (const tipo of EVENTOS_ACTIVIDAD) escuchar(s, doc, tipo, actividad);
  };
  s.engancharDocumento(document);
  engancharIframes(s);

  escuchar(s, document, "visibilitychange", () => cambioDeVisibilidad(s));
  escuchar(s, window, "pagehide", () => {
    contar(s);
    reportar(s, true);
  });

  s.timer = setInterval(() => {
    contar(s);
    engancharIframes(s);
  }, TICK_MS);
  s.reporte = setInterval(() => reportar(s, false), REPORTE_MS);

  pedirToken(s);
  return s;
}

function cerrar(s) {
  contar(s);
  reportar(s, true);
  clearInterval(s.timer);
  clearInterval(s.reporte);
  for (const [destino, tipo, fn] of s.escuchas) destino.removeEventListener(tipo, fn, true);
  s.escuchas = [];
}

function escuchar(s, destino, tipo, fn) {
  destino.addEventListener(tipo, fn, { capture: true, passive: true });
  s.escuchas.push([destino, tipo, fn]);
}

// Armonizador y Polyacordes son un iframe del mismo dominio: los clics de adentro no llegan
// al documento de afuera, así que se escucha también el de cada iframe. Se revisa en cada
// tick porque el iframe carga después y puede recargarse (y trae un documento nuevo).
function engancharIframes(s) {
  for (const iframe of document.querySelectorAll("iframe")) {
    try {
      s.engancharDocumento(iframe.contentDocument);
    } catch {
      /* iframe de otro dominio: no se puede y no hace falta */
    }
  }
}

function estaVisible() {
  return document.visibilityState === "visible";
}

/** Suma el rato desde el tick anterior, con el estado que tenía durante ese rato. */
function contar(s) {
  const ahora = performance.now();
  const dt = ahora - s.ultimo;
  s.ultimo = ahora;
  const visibleAhora = estaVisible();

  if (dt > 0 && dt <= SALTO_MAX_MS) {
    const escondidaHace = s.escondidaDesde === null ? 0 : Date.now() - s.escondidaDesde;
    if (s.visibleAntes || escondidaHace <= CORTE_SESION_MS) s.abierta += dt;
    if (s.visibleAntes) {
      s.visible += dt;
      const usoHasta = Math.min(ahora, s.ultimaActividad + VENTANA_USO_MS);
      s.usando += Math.max(0, usoHasta - (ahora - dt));
    }
  }
  s.visibleAntes = visibleAhora;
}

function cambioDeVisibilidad(s) {
  contar(s);
  if (!estaVisible()) {
    s.escondidaDesde = Date.now();
    reportar(s, true);
    return;
  }

  const escondida = s.escondidaDesde === null ? 0 : Date.now() - s.escondidaDesde;
  s.escondidaDesde = null;
  if (escondida > CORTE_SESION_MS || diaLocal() !== s.dia) {
    // Volvió después de mucho (o al otro día): es otra sesión de la misma app.
    cerrar(s);
    const nueva = nuevaSesion(s.app);
    if (actual === s) actual = nueva;
    s.reemplazo = nueva;
  }
}

async function pedirToken(s) {
  try {
    const { auth } = await import("@/lib/firebase-client");
    if (!auth) return;
    await auth.authStateReady();
    s.token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
  } catch {
    s.token = null;
  }
}

function reportar(s, final) {
  const datos = {
    sesion: s.id,
    visitante: visitante(),
    app: s.app,
    dia: s.dia,
    locale: s.locale,
    dispositivo: s.dispositivo,
    seg_abierta: Math.round(s.abierta / 1000),
    seg_visible: Math.round(s.visible / 1000),
    seg_usando: Math.round(s.usando / 1000),
    interacciones: s.interacciones,
  };
  // Menos de un segundo a la vista no es una visita (y es lo que deja el doble montaje de
  // React en desarrollo).
  if (datos.seg_visible < 1) return;
  const clave = `${datos.seg_abierta}|${datos.seg_visible}|${datos.seg_usando}|${datos.interacciones}`;
  if (clave === s.reportado) return;
  s.reportado = clave;

  // El token va hasta que un reporte con token llegó: con uno alcanza para saber de quién es.
  const conToken = !s.identificada && s.token;
  const cuerpo = JSON.stringify(conToken ? { ...datos, token: s.token } : datos);

  try {
    if (final && navigator.sendBeacon) {
      // Al cerrar la pestaña solo sale lo que va por sendBeacon. text/plain: sin preflight.
      navigator.sendBeacon(RUTA_REPORTE, new Blob([cuerpo], { type: "text/plain" }));
      return;
    }
    fetch(RUTA_REPORTE, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: cuerpo,
      keepalive: true,
    })
      .then((res) => {
        if (res.ok && conToken) s.identificada = true;
      })
      .catch(() => {
        /* se pierde este reporte; el próximo trae los totales igual */
      });
  } catch {
    /* sin red o sin fetch: lo mismo */
  }
}

function visitante() {
  try {
    const guardado = localStorage.getItem(CLAVE_VISITANTE);
    if (guardado && VISITANTE_RE.test(guardado)) return guardado;
    const nuevo = nuevoId();
    localStorage.setItem(CLAVE_VISITANTE, nuevo);
    return nuevo;
  } catch {
    // Sin localStorage (modo privado viejo): un id por carga de página.
    visitanteSinGuardar ??= nuevoId();
    return visitanteSinGuardar;
  }
}

function nuevoId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* contexto no seguro: sigue el fallback */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** El día de la persona, con su reloj: "cuántos días por mes" cuenta sus días. */
function diaLocal() {
  const d = new Date();
  const dos = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
}

function localeDeLaRuta() {
  return window.location.pathname.split("/")[1] || "";
}

function dispositivo() {
  try {
    const ua = navigator.userAgent || "";
    // El iPad con iPadOS se presenta como Mac: lo delatan los puntos táctiles. Una notebook
    // con pantalla táctil también los tiene, pero su puntero principal es fino y su UA no
    // dice Mac ni Mobi.
    const tactil =
      window.matchMedia("(pointer: coarse)").matches ||
      /Mobi|Android|iPhone|iPad/i.test(ua) ||
      (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    if (!tactil) return "compu";
    return Math.min(window.screen.width, window.screen.height) < 600 ? "celular" : "tablet";
  } catch {
    return "";
  }
}
